import api from './api';

export type EstadoPedido = 'pendiente' | 'confirmado' | 'preparando' | 'listo' | 'en_camino' | 'entregado' | 'cancelado' | 'devuelto';
export type MetodoPago = 'efectivo' | 'tarjeta' | 'transferencia' | 'mercadopago' | 'webpay' | 'khipu' | 'flow' | 'whatsapp' | 'yape' | 'plin' | 'digital';

export interface ProductoPedido {
  id: number;
  pedido: number;
  producto: number;
  producto_nombre?: string;
  producto_detalle?: {
    id: number;
    nombre: string;
    imagen?: string;
  };
  cantidad: number;
  precio_unitario: string;
  precio_total: string;
  notas?: string;
}

export interface SeguimientoPedido {
  id: number;
  pedido: number;
  estado_anterior: string;
  estado_nuevo: string;
  fecha_cambio: string;
  usuario?: number;
  notas?: string;
}

export interface Pedido {
  id: number;
  numero_pedido: string;
  codigo_seguimiento: string;
  cliente: number;
  cliente_detalle?: {
    id: number;
    nombre: string;
    apellido?: string;
    dni?: string;
    ruc?: string;
    telefono?: string;
    email: string;
    direccion?: string;
  };
  direccion_entrega: number;
  direccion_entrega_detalle?: {
    id: number;
    nombre: string;
    direccion_linea1: string;
    direccion_linea2?: string;
    ciudad: string;
    distrito: string;
    codigo_postal?: string;
    telefono_contacto?: string;
    referencia?: string;
  };
  estado: EstadoPedido;
  estado_display: string;
  estado_pago: 'pendiente' | 'procesando' | 'aprobado' | 'rechazado' | 'cancelado' | 'reembolsado';
  estado_pago_display?: string;
  repartidor?: number;
  subtotal: string;
  costo_delivery: string;
  descuento: string;
  total: string;
  metodo_pago: MetodoPago;
  pagado: boolean;
  fecha_pedido: string;
  fecha_entrega_estimada?: string;
  fecha_entrega_real?: string;
  tiempo_preparacion_estimado: number;
  notas_cliente?: string;
  items: ProductoPedido[];
  seguimiento: SeguimientoPedido[];
  puede_cancelar: boolean;
  esta_en_proceso: boolean;
}

export interface CrearPedidoData {
  direccion_id: number;  // ID de la dirección del modelo auth_app.Direccion
  metodo_pago: MetodoPago;
  notas_cliente?: string;
  cliente_id?: number;  // ID del cliente (opcional, solo para admins creando pedidos para otros clientes)
  items: Array<{
    producto: number;
    cantidad: number;
    precio_unitario: number;
  }>;
}

// Obtener todos los pedidos del usuario
export const getPedidos = async (): Promise<Pedido[]> => {
  const response = await api.get('/pedidos/pedidos-online/');
  // La API puede devolver { results: [...] } o directamente [...]
  const data = response.data;
  return Array.isArray(data) ? data : (data.results || []);
};

// Obtener un pedido por ID
export const getPedidoById = async (id: number): Promise<Pedido> => {
  const response = await api.get<Pedido>(`/pedidos/pedidos-online/${id}/`);
  return response.data;
};

// Crear un nuevo pedido
export const createPedido = async (data: CrearPedidoData): Promise<Pedido> => {
  const response = await api.post<Pedido>('/pedidos/pedidos-online/', data);
  return response.data;
};

// Cancelar un pedido
export const cancelarPedido = async (id: number, motivo?: string): Promise<Pedido> => {
  const response = await api.post<Pedido>(`/pedidos/pedidos-online/${id}/cancelar/`, { motivo });
  return response.data;
};

// Cambiar estado del pedido
export const cambiarEstadoPedido = async (id: number, estado: EstadoPedido, notas?: string): Promise<Pedido> => {
  const response = await api.post<Pedido>(`/pedidos/pedidos-online/${id}/cambiar_estado/`, { estado, notas });
  return response.data;
};

// Calificar pedido
export const calificarPedido = async (id: number, calificacion: number, comentario?: string): Promise<Pedido> => {
  const response = await api.post<Pedido>(`/pedidos/pedidos-online/${id}/calificar/`, { 
    calificacion, 
    comentario_calificacion: comentario 
  });
  return response.data;
};

// Obtener estadísticas de pedidos
export const getEstadisticasPedidos = async () => {
  const response = await api.get('/pedidos/pedidos-online/estadisticas/');
  return response.data;
};

// Función auxiliar para formatear el estado
export const getEstadoLabel = (estado: EstadoPedido): string => {
  const labels: Record<EstadoPedido, string> = {
    pendiente: 'Pendiente',
    confirmado: 'Confirmado',
    preparando: 'En Preparación',
    listo: 'Listo para Entrega',
    en_camino: 'En Camino',
    entregado: 'Entregado',
    cancelado: 'Cancelado',
    devuelto: 'Devuelto'
  };
  return labels[estado] || estado;
};

// Función auxiliar para obtener el color del estado
export const getEstadoColor = (estado: EstadoPedido): string => {
  const colors: Record<EstadoPedido, string> = {
    pendiente: 'warning',
    confirmado: 'info',
    preparando: 'info',
    listo: 'primary',
    en_camino: 'primary',
    entregado: 'success',
    cancelado: 'error',
    devuelto: 'error'
  };
  return colors[estado] || 'default';
};

// Función auxiliar para el estado de pago
export const getEstadoPagoLabel = (pagado: boolean): string => {
  return pagado ? 'Pagado' : 'Pendiente de Pago';
};
